import type { WavesChart } from '../wavesChart.js';
import type { Variant } from '../gameTypes.js';
import { VARIANTS, parseVariantSlug } from '../gameTypes.js';
import { ON_AND_ON_WAVES_VARIANTS } from './01-on-and-on.js';
import { SKY_HIGH_WAVES_VARIANTS } from './02-sky-high.js';
import { HEROES_WAVES_VARIANTS } from './03-heroes.js';
import { CRADLES_WAVES_VARIANTS } from './04-cradles.js';
import { MY_HEART_WAVES_VARIANTS } from './05-my-heart.js';
import { INVINCIBLE_WAVES_VARIANTS } from './06-invincible.js';
import { HOPE_WAVES_VARIANTS } from './07-hope.js';
import { ADVENTURE_WAVES_VARIANTS } from './08-adventure.js';
import { BLANK_WAVES_VARIANTS } from './09-blank.js';
import { DIAMOND_WAVES_VARIANTS } from './10-diamond.js';
import { HOLLAH_WAVES_VARIANTS } from './11-hollah.js';
import { NEKOZILLA_WAVES_VARIANTS } from './12-nekozilla.js';

/**
 * One playable Waves map: a single (track, variant) pair. Mirrors the
 * other modes' builtin shape so the seeder, home page, and play page
 * can iterate uniformly. Waves doesn't ship deterministic expected
 * replays today — the server validates submitted replays via
 * {@link import('../engine/waves/headlessRunner.js').runWavesReplay}.
 */
export interface WavesBuiltinMap {
  chart: WavesChart;
  expectedReplay: null;
}

export interface WavesBuiltinTrack {
  baseSlug: string;
  variants: Record<Variant, WavesBuiltinMap>;
}

const VARIANT_BUNDLES: ReadonlyArray<{
  baseSlug: string;
  charts: Record<Variant, WavesChart>;
}> = [
  // Order intentionally matches the Circles + Guitar catalogue so the
  // home page (and tier 1..5 progression labels) line up across modes.
  { baseSlug: 'on-and-on', charts: ON_AND_ON_WAVES_VARIANTS },
  { baseSlug: 'hope', charts: HOPE_WAVES_VARIANTS },
  { baseSlug: 'adventure', charts: ADVENTURE_WAVES_VARIANTS },
  { baseSlug: 'sky-high', charts: SKY_HIGH_WAVES_VARIANTS },
  { baseSlug: 'cradles', charts: CRADLES_WAVES_VARIANTS },
  { baseSlug: 'diamond', charts: DIAMOND_WAVES_VARIANTS },
  { baseSlug: 'blank', charts: BLANK_WAVES_VARIANTS },
  { baseSlug: 'heroes', charts: HEROES_WAVES_VARIANTS },
  { baseSlug: 'hollah', charts: HOLLAH_WAVES_VARIANTS },
  { baseSlug: 'my-heart', charts: MY_HEART_WAVES_VARIANTS },
  { baseSlug: 'nekozilla', charts: NEKOZILLA_WAVES_VARIANTS },
  { baseSlug: 'invincible', charts: INVINCIBLE_WAVES_VARIANTS },
];

export const BUILTIN_WAVES_TRACKS: WavesBuiltinTrack[] = VARIANT_BUNDLES.map(
  (bundle) => ({
    baseSlug: bundle.baseSlug,
    variants: Object.fromEntries(
      VARIANTS.map((v) => [
        v,
        {
          chart: bundle.charts[v],
          expectedReplay: null,
        } satisfies WavesBuiltinMap,
      ]),
    ) as Record<Variant, WavesBuiltinMap>,
  }),
);

export const BUILTIN_WAVES_MAPS: WavesBuiltinMap[] = BUILTIN_WAVES_TRACKS.flatMap(
  (track) => VARIANTS.map((v) => track.variants[v]),
);

/**
 * Look up a Waves builtin by slug (`'invincible'`,
 * `'invincible-hard'`, `'invincible-extreme'`). Mirrors the per-mode
 * lookups in Pulse / Circles / Piano / Guitar — literal slug matches
 * first, then a variant-suffix parse, so a hypothetical track whose
 * slug ends in `-hard` won't be misparsed.
 */
export function getBuiltinWavesMapBySlug(slug: string): WavesBuiltinMap | undefined {
  const literalTrack = BUILTIN_WAVES_TRACKS.find((t) => t.baseSlug === slug);
  if (literalTrack) {
    return literalTrack.variants.normal;
  }
  const { baseSlug, variant } = parseVariantSlug(slug);
  const track = BUILTIN_WAVES_TRACKS.find((t) => t.baseSlug === baseSlug);
  if (!track) {
    return undefined;
  }
  return track.variants[variant];
}

export function listBuiltinWavesTracks(): WavesBuiltinTrack[] {
  return BUILTIN_WAVES_TRACKS;
}

export function listBuiltinWavesMaps(): WavesBuiltinMap[] {
  return BUILTIN_WAVES_MAPS;
}
